import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import LoadingOverlay from '@chama/ui-components/components/LoadingOverlay';
import MapNotifications, {
  withNotifications,
} from '@chama/ui-components/components/MapNotifications';

import Map from './components/Map';
import MapControls from './components/MapControls';
import SaveControls from './components/SaveControls';
import { mapSavedMessage, mapSaveFailedMessage } from './components/notificationMessages';

import './CoverageAreaMap.css';

const mapControlModes = {
  addPolygonMode: false,
  deletePolygonMode: false,
  editPolygonMode: false,
  dragPolygonMode: false,
};

class CoverageAreaMap extends Component {
  state = {
    editMapMode: false,
    ...mapControlModes,
    // eslint-disable-next-line react/no-unused-state
    initialData: null,
    historyPoint: 0,
    history: [],
    boundsFit: false,
    isSubmitting: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (props.data.geoJson !== state.initialData) {
      return {
        initialData: props.data.geoJson,
        history: [props.data.geoJson],
        historyPoint: 0,
        boundsFit: false,
        ...mapControlModes,
        editMapMode: false,
      };
    }

    return null;
  }

  handleEditModeTurnOn = () => this.setState({ editMapMode: true });

  handleMapAddModeToggle = () => {
    this.setState(prevState => ({ ...mapControlModes, addPolygonMode: !prevState.addPolygonMode }));
  };

  handleMapDeleteModeToggle = () => {
    this.setState(prevState => ({
      ...mapControlModes,
      deletePolygonMode: !prevState.deletePolygonMode,
    }));
  };

  handleMapEditModeToggle = () => {
    this.setState(prevState => ({
      ...mapControlModes,
      editPolygonMode: !prevState.editPolygonMode,
    }));
  };

  handleMapDragModeToggle = () => {
    this.setState(prevState => ({
      ...mapControlModes,
      dragPolygonMode: !prevState.dragPolygonMode,
    }));
  };

  handleAdditionToHistory = currentMapState => {
    const { history, historyPoint } = this.state;
    let copy = [...history];
    if (historyPoint > 0) {
      copy = copy.slice(historyPoint);
      this.setState({ historyPoint: 0 });
    }
    this.setState({ history: [currentMapState, ...copy] });
  };

  handleGoingBackInHistory = () => {
    const { history, historyPoint } = this.state;
    const nextHistoryPoint = historyPoint + 1;

    if (nextHistoryPoint >= history.length) return;

    this.setState({ historyPoint: nextHistoryPoint });
  };

  handleGoingForwardInHistory = () => {
    const { historyPoint } = this.state;
    const nextHistoryPoint = historyPoint - 1;
    if (nextHistoryPoint < 0) return;

    this.setState({ historyPoint: nextHistoryPoint });
  };

  handleFitBounds = boundsFit => this.setState({ boundsFit });

  handleCancel = () => {
    const { initialData } = this.state;
    const { onCancelEdition } = this.props;

    onCancelEdition();

    this.setState({
      editMapMode: false,
      history: [initialData],
      historyPoint: 0,
      boundsFit: false,
      ...mapControlModes,
    });
  };

  handleSave = () => {
    const { onSave, addNotification } = this.props;
    const { history, historyPoint } = this.state;
    const selectedMapState = history[historyPoint];
    this.setState({ isSubmitting: true }, () => {
      onSave(selectedMapState)
        .then(() => {
          this.setState({
            editMapMode: false,
            isSubmitting: false,
            boundsFit: false,
            history: [selectedMapState],
            ...mapControlModes,
          });
          addNotification(mapSavedMessage());
        })
        .catch(e => {
          this.setState({ isSubmitting: false });
          addNotification(mapSaveFailedMessage());
          console.log('ERROR');
          console.log(e);
        });
    });
  };

  render() {
    const {
      googleMapsApiKey,
      editable,
      isFullScreenView,
      onToggleMapView,
      classes,
      notifications,
    } = this.props;

    const {
      editMapMode,
      history,
      historyPoint,
      addPolygonMode,
      deletePolygonMode,
      editPolygonMode,
      dragPolygonMode,
      boundsFit,
      isSubmitting,
    } = this.state;

    const canUndo = history.length > 1 && historyPoint !== history.length - 1;
    const canRedo = historyPoint > 0;
    const canSave = canUndo;

    return (
      <Fragment>
        {isSubmitting && <LoadingOverlay />}
        <div className={classnames('CoverageAreaMap', classes.wrapper)}>
          <div className="CoverageAreaMap__controls-container">
            {editMapMode && (
              <MapControls
                activeControls={this.state}
                undoEnabled={canUndo}
                onUndoClick={this.handleGoingBackInHistory}
                redoEnabled={canRedo}
                onRedoClick={this.handleGoingForwardInHistory}
                onAddClick={this.handleMapAddModeToggle}
                onDeleteClick={this.handleMapDeleteModeToggle}
                onEditClick={this.handleMapEditModeToggle}
                onDragClick={this.handleMapDragModeToggle}
                onToggleMapView={onToggleMapView}
                isFullScreenView={isFullScreenView}
              />
            )}
            <MapNotifications notifications={notifications} />
          </div>
          <Map
            data={history[historyPoint]}
            googleMapsApiKey={googleMapsApiKey}
            editMapMode={editMapMode}
            editPolygonMode={editPolygonMode}
            dragPolygonMode={dragPolygonMode}
            addPolygonMode={addPolygonMode}
            deletePolygonMode={deletePolygonMode}
            onAddToHistory={this.handleAdditionToHistory}
            boundsFit={boundsFit}
            onFitBounds={this.handleFitBounds}
          />
          {editable && (
            <SaveControls
              classes={classnames(classes.editButtonsContainer, {
                'CoverageAreaMap__buttons--expanded-view': isFullScreenView,
              })}
              saveDisabled={!canSave}
              editModeEnabled={editMapMode}
              onEnableEditMode={this.handleEditModeTurnOn}
              onSubmit={this.handleSave}
              onCancel={this.handleCancel}
            />
          )}
        </div>
      </Fragment>
    );
  }
}
CoverageAreaMap.propTypes = {
  googleMapsApiKey: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  onSave: PropTypes.func,
  onToggleMapView: PropTypes.func,
  onCancelEdition: PropTypes.func,
  isFullScreenView: PropTypes.bool,
  classes: PropTypes.shape({
    wrapper: PropTypes.string,
    editButtonsContainer: PropTypes.string,
  }),
  notifications: PropTypes.object.isRequired,
  addNotification: PropTypes.func.isRequired,
};
CoverageAreaMap.defaultProps = {
  editable: false,
  onSave: Function.prototype,
  onCancelEdition: Function.prototype,
  onToggleMapView: Function.prototype,
  isFullScreenView: false,
  classes: {
    wrapper: '',
    editButtonsContainer: '',
  },
};
/* eslint-enable react/no-unused-prop-types */

export default withNotifications(CoverageAreaMap);



// WEBPACK FOOTER //
// ../ui-components/components/CoverageAreaMap/CoverageAreaMap.jsx